// The /tasks filters live in the URL (constitution §5.6), so they survive a reload, the Back button
// and a shared link. Every value is checked here; anything else in the URL is dropped.
import { isIsoDate, TASK_STATUSES, type TaskStatus } from '@cis/shared';
import type { TaskListParams } from '@cis/api-client';

const SMALL_PAGE = 25;
const MEDIUM_PAGE = 50;
const LARGE_PAGE = 100; // the API's pageSize limit (constitution §4.13)
export const PAGE_SIZES = [SMALL_PAGE, MEDIUM_PAGE, LARGE_PAGE] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = MEDIUM_PAGE;

export const PHASES = ['P0', 'P1', 'P2', 'P3', 'P4'] as const;
export const TIER_LETTERS = ['A', 'B', 'C', 'D', 'E'] as const;
export const FAMILY_COUNT = 18;
export const VIEWS = ['my-week', 'overdue'] as const;

const MAX_PAGE = 10_000;
// Page 1 is the default and is kept out of the URL, so only later pages are read from it.
const FIRST_LISTED_PAGE = 2;
const MAX_AREA = 64;
const MAX_QUERY = 100;

export interface TaskSearch {
  readonly page?: number | undefined;
  readonly pageSize?: PageSize | undefined;
  readonly phase?: (typeof PHASES)[number] | undefined;
  readonly area?: string | undefined;
  readonly family?: number | undefined;
  readonly tier?: (typeof TIER_LETTERS)[number] | undefined;
  readonly assignee?: 'me' | number | undefined;
  /** Comma-separated statuses, e.g. "blocked,in_progress". */
  readonly status?: string | undefined;
  readonly dueFrom?: string | undefined;
  readonly dueTo?: string | undefined;
  readonly q?: string | undefined;
  readonly view?: (typeof VIEWS)[number] | undefined;
  readonly retired?: true | undefined;
}

const intIn = (v: unknown, min: number, max: number): number | undefined => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined =>
  list.find((item) => item === v);

// The router parses "123" in the URL as a number; text filters take it back as text.
const textUpTo = (v: unknown, max: number): string | undefined => {
  if (typeof v !== 'string' && typeof v !== 'number') return undefined;
  const text = String(v).trim();
  return text === '' || text.length > max ? undefined : text;
};

const dateOf = (v: unknown): string | undefined =>
  typeof v === 'string' && isIsoDate(v) ? v : undefined;

/** The statuses in a comma list, known ones only, in the status machine's order. */
export function statusList(raw: string | undefined): TaskStatus[] {
  if (raw === undefined) return [];
  const parts = new Set(raw.split(','));
  return TASK_STATUSES.filter((s) => parts.has(s));
}

const statusOf = (v: unknown): string | undefined => {
  const list = statusList(typeof v === 'string' ? v : undefined);
  return list.length === 0 ? undefined : list.join(',');
};

const assigneeOf = (v: unknown): 'me' | number | undefined =>
  v === 'me' ? 'me' : intIn(v, 1, Number.MAX_SAFE_INTEGER);

/** Every key is returned, so an unchecked raw value cannot survive the router's merge. */
export function validateTaskSearch(search: Record<string, unknown>): TaskSearch {
  return {
    page: intIn(search['page'], FIRST_LISTED_PAGE, MAX_PAGE),
    pageSize: PAGE_SIZES.find((s) => s === intIn(search['pageSize'], 1, MAX_PAGE)),
    phase: oneOf(PHASES, search['phase']),
    area: textUpTo(search['area'], MAX_AREA),
    family: intIn(search['family'], 1, FAMILY_COUNT),
    tier: oneOf(TIER_LETTERS, search['tier']),
    assignee: assigneeOf(search['assignee']),
    status: statusOf(search['status']),
    dueFrom: dateOf(search['dueFrom']),
    dueTo: dateOf(search['dueTo']),
    q: textUpTo(search['q'], MAX_QUERY),
    view: oneOf(VIEWS, search['view']),
    retired: search['retired'] === true || search['retired'] === 'true' ? true : undefined,
  };
}

/** The API query for these filters. */
export function listParamsOf(s: TaskSearch): TaskListParams {
  return {
    page: s.page ?? 1,
    pageSize: s.pageSize ?? DEFAULT_PAGE_SIZE,
    phase: s.phase,
    area: s.area,
    family: s.family,
    tier: s.tier,
    assignee: s.assignee,
    status: statusList(s.status),
    dueFrom: s.dueFrom,
    dueTo: s.dueTo,
    q: s.q,
    view: s.view,
    includeRetired: s.retired === true,
  };
}

const FILTER_KEYS = [
  'phase',
  'area',
  'family',
  'tier',
  'assignee',
  'status',
  'dueFrom',
  'dueTo',
  'q',
  'view',
  'retired',
] as const satisfies readonly (keyof TaskSearch)[];

export function hasFilters(s: TaskSearch): boolean {
  return FILTER_KEYS.some((k) => s[k] !== undefined);
}
